import { useState, useCallback, useRef, useEffect } from 'react';

const MIN_COL_WIDTH = 20;

const useResizableColumns = (storageKey, defaultWidths) => {
  const [widths, setWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === defaultWidths.length) return parsed;
      }
    } catch {}
    return defaultWidths;
  });

  const dragging = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(widths)); } catch {}
  }, [storageKey, widths]);

  const onMouseDown = useCallback((colIndex, e) => {
    e.preventDefault();
    const rightNeighbor = colIndex + 1;
    if (rightNeighbor >= widths.length) return;

    const startX = e.clientX;
    const startLeft = widths[colIndex];
    const startRight = widths[rightNeighbor];

    const onMouseMove = (moveE) => {
      const delta = moveE.clientX - startX;
      const newLeft = startLeft + delta;
      const newRight = startRight - delta;

      if (newLeft < MIN_COL_WIDTH || newRight < MIN_COL_WIDTH) return;

      setWidths(prev => {
        const next = [...prev];
        next[colIndex] = newLeft;
        next[rightNeighbor] = newRight;
        return next;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragging.current = null;
    };

    dragging.current = colIndex;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [widths]);

  const resetWidths = useCallback(() => {
    setWidths(defaultWidths);
    try { localStorage.removeItem(storageKey); } catch {}
  }, [defaultWidths, storageKey]);

  const totalWidth = widths.reduce((s, w) => s + w, 0);
  const percentages = widths.map(w => (w / totalWidth * 100) + '%');

  return { widths, percentages, onMouseDown, resetWidths };
};

export default useResizableColumns;
